import React from 'react'
import {
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
  Elements,
} from '@recurly/react-recurly'
import {
  Button,
  Icon,
  ParagraphText,
  Checkbox,
  Select,
} from '@thryvlabs/maverick'
import rawCountryStates from 'iso3166-2-db/i18n/en'
import {
  FormFieldContainer,
  TaxesPromptContainer,
} from '../ui-view/payment-information'
// import {useFlags} from 'launchdarkly-react-client-sdk'
import {AutopayTerms} from './autopay-terms'
import {useBillingInfo} from './keap-partner/payment-information-components/contexts/billing-info-context'
import {usePaymentAddress} from './keap-partner/payment-information-components/contexts/payment-address-context'
import {usePaymentCheckboxes} from './keap-partner/payment-information-components/contexts/payment-checkboxes-context'
import {useRecurlyPayment} from './keap-partner/payment-information-components/contexts/recurly-payment-context'
import {usePaymentCard} from './keap-partner/payment-information-components/contexts/payment-card-context'
import {COUNTRIES} from './keap-partner/payment-information-components/constants'
import {
  CardOnFileForm,
  ChangeToCardOnFileButton,
} from './keap-partner/payment-information-components/card-on-file-form'
import {useDispatch, useSelector} from 'react-redux'
import {nextPaymentStep, setPaymentStep} from '../redux/paymentScreenViewSlice'
// import {usePaymentOrder} from './keap-partner/payment-information-components/hooks/use-payment-order'

const RecurlyPaymentForm = ({
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection,
  showSecondSection,
  nextStep,
  setName,
  businessAddress,
  country,
  setIsAddressUpdated,
  bizphone,
  isAddressUpdated,
  setPaymentRequestState,
  setCurrentStep,
}) => {
  const is5Order = true
  const {isMobileViewEnable} = useSelector(state => state.screenView)
  const {billingInfo, tryCardOnFile, isCVCVerified} = useBillingInfo()
  const [selectedCountry, setSelectedCountry] = React.useState(
    COUNTRIES.find(({value}) => value === country),
  )
  const {
    addressInformation,
    isValidAddress,
    updateAddressField,
    resetAddress,
    setFromBusinessAddress,
  } = usePaymentAddress()
  const {clearRecurlyToken, fetchToken, recurlyToken} = useRecurlyPayment()
  const {isValidCard, updateValidation} = usePaymentCard()
  const {checkBoxesValues, setCheckBoxesValues} = usePaymentCheckboxes()
  const [listOfStates, setListOfStates] = React.useState([])
  const formRef = React.useRef()

  const {useBusinessAddressInfo} = checkBoxesValues
  const dispatch = useDispatch()

  // const {frontendUpdatedTerms} = useFlags()
  const frontendUpdatedTerms = true

  const isAusNZ = ['AU', 'NZ'].includes(country)

  const handleOnchange = ({target: {value, name}}) => {
    setCheckBoxesValues({
      ...checkBoxesValues,
      agreedTerms: false,
    })
    clearRecurlyToken()
    updateAddressField(name, value)
    if (!isMobileViewEnable) setIsAddressUpdated(false)
  }

  React.useEffect(() => {
    if (!isCVCVerified) return undefined
    dispatch(setPaymentStep(4))
  }, [dispatch, isCVCVerified])

  React.useEffect(() => {
    if (selectedCountry.value !== country) {
      setSelectedCountry(COUNTRIES.find(({value}) => country === value))
    }
    const getStates = () => {
      let stateLabel = ''
      if (['US', 'AU', 'NZ'].includes(selectedCountry.value)) {
        stateLabel = 'State'
      } else if (selectedCountry.value === 'KY') {
        stateLabel = 'City'
      } else if (selectedCountry.value === 'CA') {
        stateLabel = 'Province'
      } else if (selectedCountry.value === 'BB') {
        stateLabel = 'Parish'
      }
      let blankOption = [{name: 'Select ' + stateLabel, value: ''}]
      const states = rawCountryStates[selectedCountry.value].regions
        .map(({iso, name}) => ({
          name,
          value: iso,
        }))
        .sort((a, b) => (a.name > b.name ? 1 : -1))
      setListOfStates([...blankOption, ...states])
    }
    if (selectedCountry.value) {
      getStates(selectedCountry.value)
    }
  }, [selectedCountry, country])

  React.useEffect(() => {
    if (useBusinessAddressInfo) {
      if (checkBoxesValues.updateBillingAddress) {
        setCheckBoxesValues({...checkBoxesValues, updateBillingAddress: false})
      }
      setFromBusinessAddress(businessAddress)
    } else {
      resetAddress()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBusinessAddressInfo, businessAddress, listOfStates])

  React.useEffect(() => {
    if (!checkBoxesValues.agreedTerms) return undefined
    if (recurlyToken) return undefined
    if (billingInfo && tryCardOnFile) return undefined
    fetchToken(formRef.current)
  }, [recurlyToken, checkBoxesValues.agreedTerms, billingInfo, tryCardOnFile])

  if (billingInfo && tryCardOnFile) {
    return (
      <CardOnFileForm
        setCalculatingTaxes={setCalculatingTaxes}
        setName={setName}
        setIsAddressUpdated={setIsAddressUpdated}
        calculatingTaxes={calculatingTaxes}
        setPaymentRequestState={setPaymentRequestState}
      />
    )
  }

  if (isMobileViewEnable) {
    return (
      <form
        onSubmit={e => {
          e.preventDefault()
          // nextStep()
          // dispatch(nextPaymentStep())
        }}
        ref={formRef}
      >
        <ChangeToCardOnFileButton />

        <div style={{display: showFirstSection ? 'block' : 'none'}}>
          <CardNumberElement
            className="recurly-element-inline"
            style={{
              fontSize: '18px',
              width: '100%',
              placeholder: {content: 'Card number'},
            }}
            onChange={e => {
              updateValidation('cardNumber', e.valid)
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            displayIcon={true}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <div className="d-flex justify-content-between">
            <CardMonthElement
              className="recurly-element-month"
              inputType="mobileSelect"
              style={{fontSize: '18', placeholder: {content: 'MM'}}}
              onChange={e => {
                updateValidation('cardMonth', e.valid)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
            <CardYearElement
              className="recurly-element-inline ml-2"
              inputType="mobileSelect"
              style={{fontSize: '18', placeholder: {content: 'YY'}}}
              onChange={e => {
                updateValidation('cardYear', e.valid)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <div className="d-flex justify-content-between">
            <CardCvvElement
              className="recurly-element-inline mr-2"
              style={{
                fontSize: '18',
                placeholder: {content: 'CVV'},
              }}
              onChange={e => {
                updateValidation('cardCVV', e.valid)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
            <input
              type="text"
              placeholder="Postal Code"
              data-recurly="postal_code"
              style={{
                fontSize: '18',
                width: '96%',
              }}
              onChange={e => {
                handleOnchange(e)
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              className="recurly-element recurly-address"
              name="postalCode"
              value={addressInformation.postalCode}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <Select
            options={COUNTRIES}
            selectLabel="Country"
            width="full"
            className="recurly-element recurly-address mt-3"
            data-recurly="country"
            onChange={e => {
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            selectedOption={selectedCountry}
            setSelectedOption={setSelectedCountry}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <input
            hidden
            readOnly
            type="text"
            placeholder="Country"
            value={selectedCountry.value}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="country"
          />
          <input
            hidden
            readOnly
            type="text"
            placeholder="Phone"
            value={bizphone}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="phone"
          />
          <div className="d-flex justify-content-center mb-4 mt-4">
            <Button
              variant="primary"
              onClick={() => {
                dispatch(nextPaymentStep())
              }}
              className="step__container__button"
              disabled={!isValidCard}
            >
              Continue
            </Button>
          </div>
        </div>

        <div style={{display: showSecondSection ? 'block' : 'none'}}>
          {!isAusNZ && (
            <div
              className="d-flex info__alert gap-2 px-2"
              style={{
                background: 'rgb(230, 242, 255)',
                border: '1px solid rgb(193, 222, 255)',
                borderRadius: '5px',
              }}
            >
              <Icon type="solid" variant="infoCircle" color="#057aff" />
              <ParagraphText
                variant="reg"
                style={{marginTop: '10px', fontSize: '12px'}}
              >
                The current estimated taxes are based on your business address.
              </ParagraphText>
            </div>
          )}
          <div className="mt-4 mb-2">
            <Checkbox
              hasLabel
              name="useBusinessAddressInfo"
              disabled={checkBoxesValues.agreedTerms}
              checked={checkBoxesValues.useBusinessAddressInfo}
              onChange={e => {
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  useBusinessAddressInfo: e.target.checked,
                  agreedTerms: false,
                })
              }}
              label="Same as my business address"
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <div className="d-flex justify-content-between">
            <input
              style={{width: ' 49%'}}
              onChange={handleOnchange}
              className="recurly-element recurly-input"
              data-recurly="first_name"
              placeholder="First Name"
              name="firstName"
              value={addressInformation.firstName}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
            <input
              style={{width: '49%'}}
              className="recurly-element recurly-input"
              data-recurly="last_name"
              placeholder="Last Name"
              name="lastName"
              value={addressInformation.lastName}
              onChange={handleOnchange}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                  : undefined
              }
            />
          </div>
          <input
            type="text"
            className="recurly-element recurly-address"
            data-recurly="address1"
            placeholder="Address"
            style={{
              fontSize: '18px',
            }}
            name="address"
            value={addressInformation.address}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <input
            type="text"
            className="recurly-element recurly-input mt-2.8"
            data-recurly="city"
            placeholder="City"
            style={{
              fontSize: '18px',
            }}
            name="city"
            value={addressInformation.city}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          <select
            className="recurly-element recurly-address mt-2.8"
            value={addressInformation?.state}
            onChange={e => {
              handleOnchange({
                target: {
                  name: 'state',
                  value: e.target.selectedOptions[0].value,
                },
              })
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          >
            {listOfStates.map(({name, value}) => {
              return (
                <option key={value} value={value}>
                  {name}
                </option>
              )
            })}
          </select>
          <input
            hidden
            readOnly
            value={addressInformation?.state}
            type="text"
            className="recurly-element"
            data-recurly="state"
            placeholder="State"
            style={{
              fontSize: '18px',
            }}
          />

          <div className="mt-4 mb-2">
            <Checkbox
              hasLabel
              name="updateBillingAddress"
              disabled={checkBoxesValues.agreedTerms || useBusinessAddressInfo}
              checked={checkBoxesValues.updateBillingAddress}
              onChange={e => {
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  updateBillingAddress: e.target.checked,
                  agreedTerms: false,
                })
                clearRecurlyToken()
              }}
              label="Update my billing address"
            />
          </div>

          <div className="d-flex justify-content-center mb-4 mt-4">
            <Button
              variant="primary"
              type="submit"
              className="step__container__button"
              onClick={() => {
                dispatch(nextPaymentStep())
              }}
              disabled={!isValidAddress}
            >
              Continue
            </Button>
          </div>
        </div>
      </form>
    )
  }

  return (
    <form ref={formRef}>
      <ChangeToCardOnFileButton />
      <div
        className="d-flex justify-content-between mb-1"
        style={{
          marginTop: is5Order && !isAusNZ && !billingInfo ? '10px' : '0',
        }}
      >
        <input
          className="recurly-element recurly-input mr-2 w-100"
          style={{height: is5Order ? '35px' : '45px'}}
          data-recurly="first_name"
          placeholder="First Name"
          name="firstName"
          onChange={handleOnchange}
          value={addressInformation.firstName}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
        <input
          className="recurly-element recurly-input w-100"
          style={{height: is5Order ? '35px' : '45px'}}
          data-recurly="last_name"
          placeholder="Last Name"
          name="lastName"
          onChange={handleOnchange}
          value={addressInformation.lastName}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
      </div>
      <FormFieldContainer className="grid grid-cols-5 gap-2">
        <CardNumberElement
          onChange={e => {
            updateValidation('cardNumber', e.valid)
            clearRecurlyToken()
            setCheckBoxesValues({
              ...checkBoxesValues,
              agreedTerms: false,
            })
          }}
          className="recurly-element-inline col-span-2"
          style={{
            placeholder: {content: 'Card number'},
          }}
          displayIcon={true}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />

        <CardMonthElement
          onChange={e => {
            updateValidation('cardMonth', e.valid)
            clearRecurlyToken()
            setCheckBoxesValues({
              ...checkBoxesValues,
              agreedTerms: false,
            })
          }}
          className="card-info-input w-full"
          style={{fontSize: '16', placeholder: {content: 'MM'}}}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
        <CardYearElement
          onChange={e => {
            updateValidation('cardYear', e.valid)
            clearRecurlyToken()
            setCheckBoxesValues({
              ...checkBoxesValues,
              agreedTerms: false,
            })
          }}
          className="card-info-input w-full"
          style={{fontSize: '16', width: '100%', placeholder: {content: 'YY'}}}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
        <CardCvvElement
          onChange={e => {
            updateValidation('cardCVV', e.valid)
            clearRecurlyToken()
            setCheckBoxesValues({
              ...checkBoxesValues,
              agreedTerms: false,
            })
          }}
          className="card-info-input w-full"
          style={{fontSize: '16', placeholder: {content: 'CVV'}}}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
      </FormFieldContainer>
      {is5Order && frontendUpdatedTerms && <AutopayTerms />}
      {is5Order && (
        <ParagraphText variant="lg" className="payment-method-title">
          Billing Address
        </ParagraphText>
      )}
      {is5Order && isAddressUpdated && (
        <TaxesPromptContainer>
          <Icon type="regular" variant="infoCircle" color="#057AFF" />
          <ParagraphText variant="reg" className="m-0">
            The current estimated taxes are based on your business address.
          </ParagraphText>
        </TaxesPromptContainer>
      )}
      {is5Order && (
        <div
          className="d-flex align-items-center mt-3 mb-1"
          style={{columnGap: '8px'}}
        >
          <Checkbox
            name="useBusinessAddressInfo"
            disabled={checkBoxesValues.agreedTerms}
            checked={checkBoxesValues.useBusinessAddressInfo}
            onChange={e => {
              setCheckBoxesValues({
                ...checkBoxesValues,
                useBusinessAddressInfo: e.target.checked,
                agreedTerms: false,
              })
              clearRecurlyToken()
            }}
          />
          <ParagraphText variant="reg" style={{margin: '0 0 0.7rem 0'}}>
            Same as my business address
          </ParagraphText>
        </div>
      )}
      {!is5Order && (
        <Select
          options={COUNTRIES}
          selectLabel="Country"
          width="full"
          className="recurly-element recurly-address mt-3"
          data-recurly="country"
          onChange={() => {
            clearRecurlyToken()
            setCheckBoxesValues({
              ...checkBoxesValues,
              agreedTerms: false,
            })
          }}
          selectedOption={selectedCountry}
          setSelectedOption={setSelectedCountry}
          disabled={checkBoxesValues.agreedTerms}
        />
      )}
      <input
        hidden
        readOnly
        type="text"
        placeholder="Country"
        value={selectedCountry.value}
        style={{
          fontSize: '18px',
        }}
        className="recurly-element recurly-address ml-2"
        data-recurly="country"
      />
      <input
        type="text"
        className="recurly-element"
        data-recurly="address1"
        placeholder="Street Address"
        style={{
          fontSize: '18px',
          width: '100%',
        }}
        name="address"
        value={addressInformation.address}
        onChange={handleOnchange}
        disabled={checkBoxesValues.agreedTerms}
        title={
          checkBoxesValues.agreedTerms
            ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
            : undefined
        }
      />
      <div className="grid grid-cols-3 gap-2">
        <input
          type="text"
          className="recurly-element"
          data-recurly="city"
          placeholder="City"
          style={{
            fontSize: '16px',
          }}
          name="city"
          value={addressInformation.city}
          onChange={handleOnchange}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
        <select
          className="recurly-element"
          value={addressInformation?.state}
          onChange={e => {
            handleOnchange({
              target: {
                name: 'state',
                value: e.target.selectedOptions[0].value,
              },
            })
            clearRecurlyToken()
            setCheckBoxesValues({
              ...checkBoxesValues,
              agreedTerms: false,
            })
          }}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        >
          {listOfStates.map(({name, value}) => {
            return (
              <option key={value} value={value}>
                {name}
              </option>
            )
          })}
        </select>
        <input
          hidden
          readOnly
          value={addressInformation?.state}
          type="text"
          className="recurly-element"
          data-recurly="state"
          placeholder="State"
          style={{
            fontSize: '16px',
          }}
        />
        <input
          type="text"
          className="recurly-element"
          data-recurly="postal_code"
          placeholder="Postal Code"
          style={{
            fontSize: '16px',
          }}
          name="postalCode"
          value={addressInformation.postalCode}
          onChange={handleOnchange}
          disabled={checkBoxesValues.agreedTerms}
          title={
            checkBoxesValues.agreedTerms
              ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
              : undefined
          }
        />
      </div>
      {is5Order && (
        <div
          style={{
            opacity: '0.6',
            pointerEvents: 'none',
          }}
        >
          <Select
            options={COUNTRIES}
            selectLabel="Country"
            width="full"
            className="recurly-element h-auto pt-3"
            style={{
              height: '45px !important',
              border: 'none !important',
            }}
            data-recurly="country"
            onChange={e => {
              clearRecurlyToken()
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            selectedOption={selectedCountry}
            setSelectedOption={setSelectedCountry}
            disabled={checkBoxesValues.agreedTerms}
          />
        </div>
      )}
      {!is5Order && (
        <div className="mt-4 mb-2">
          <Checkbox
            hasLabel
            name="useBusinessAddressInfo"
            disabled={checkBoxesValues.agreedTerms}
            checked={checkBoxesValues.useBusinessAddressInfo}
            onChange={e => {
              setCheckBoxesValues({
                ...checkBoxesValues,
                useBusinessAddressInfo: e.target.checked,
                agreedTerms: false,
              })
              clearRecurlyToken()
            }}
            label="Same as my business address"
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box above.'
                : undefined
            }
          />
          {frontendUpdatedTerms && <AutopayTerms styles={{marginTop: 0}} />}
        </div>
      )}
    </form>
  )
}

const PaymentForm = ({
  nextStep,
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection = false,
  showSecondSection = false,
  checkBoxesValues,
  setCheckBoxesValues,
  setName,
  businessAddress,
  country,
  handleCompleteOrder,
  setIsAddressUpdated,
  bizphone,
  is5Order,
  isAddressUpdated,
  paymentPreview,
  paymentRequestState,
  setPaymentRequestState,
  setCurrentStep,
}) => {
  return (
    <Elements>
      <RecurlyPaymentForm
        calculatingTaxes={calculatingTaxes}
        setCalculatingTaxes={setCalculatingTaxes}
        showFirstSection={showFirstSection}
        showSecondSection={showSecondSection}
        nextStep={nextStep}
        checkBoxesValues={checkBoxesValues}
        setCheckBoxesValues={setCheckBoxesValues}
        setName={setName}
        businessAddress={businessAddress}
        country={country}
        handleCompleteOrder={handleCompleteOrder}
        setIsAddressUpdated={setIsAddressUpdated}
        bizphone={bizphone}
        is5Order={is5Order}
        isAddressUpdated={isAddressUpdated}
        paymentPreview={paymentPreview}
        paymentRequestState={paymentRequestState}
        setPaymentRequestState={setPaymentRequestState}
        setCurrentStep={setCurrentStep}
      />
    </Elements>
  )
}

export {PaymentForm}
